import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * A modal panel over the page.
 *
 * The app's first real dialog. The unit switcher in {@link AppHeader} is a `role="menu"` dropdown
 * — no backdrop, no trap, dismissed by picking something — which is a different thing and is
 * deliberately left alone.
 *
 * The elevation is `--elevation-overlay`, one of the three the design system allows, and the same
 * one the switcher uses. There is no fourth.
 *
 * Everything here is the accessibility contract rather than decoration: a dialog that cannot be
 * escaped, or that leaks Tab into the page behind it, is worse than no dialog. In particular the
 * focus is *restored to the trigger* on close — without that, dismissing the panel drops keyboard
 * users at the top of the document, which on a long thread means scrolling back to where they were.
 */
export function Overlay({
  title,
  onClose,
  children,
  presentation = 'sheet',
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /**
   * `sheet` is the bottom-anchored panel every dialog in the product uses. `lightbox` is the same
   * dialog wearing a different frame: centred, unbounded in width, and with no paper behind the
   * content, because a landscape campout photograph in a 30rem card at the bottom of the screen is
   * a thumbnail with extra steps.
   *
   * **A variant rather than a second component.** CLAUDE.md makes this the only modal, and the
   * reason is the contract above — Escape, the Tab trap, the scroll lock, focus restored to the
   * trigger. A sibling `PhotoLightbox` dialog would be a second copy of all four, and the copy is
   * the one that quietly loses a behaviour.
   */
  presentation?: 'sheet' | 'lightbox';
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<Element | null>(null);

  // Captured before the panel takes focus, so closing puts the caller back where they were.
  useEffect(() => {
    restoreFocusTo.current = document.activeElement;
    return () => {
      if (restoreFocusTo.current instanceof HTMLElement) {
        restoreFocusTo.current.focus();
      }
    };
  }, []);

  // The page behind must not scroll under the panel. Restored to whatever it was rather than
  // hard-coded back to "visible", so nesting or a page that sets its own overflow survives.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const focusables = useCallback(
    () =>
      [
        ...(panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? []),
      ].filter((el) => el.offsetParent !== null),
    [],
  );

  useEffect(() => {
    focusables()[0]?.focus();
  }, [focusables]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      // Wrap at the ends. Without this Tab walks out of the panel and into the thread behind it,
      // which is still rendered and still full of links.
      const items = focusables();
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !panelRef.current?.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, focusables]);

  const lightbox = presentation === 'lightbox';

  return createPortal(
    <div
      // The backdrop dismisses, but only when the press starts and ends on the backdrop itself:
      // a drag that begins on a name inside the panel and releases outside must not close it.
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className={`fixed inset-0 flex justify-center ${lightbox ? 'items-center' : 'items-end'}`}
      style={{
        // The lightbox scrim is heavier: a photograph has to sit on something that is plainly not
        // the page, and 42% leaves the thread legible behind it.
        background: lightbox
          ? 'color-mix(in srgb, var(--color-brand-ink) 88%, transparent)'
          : 'color-mix(in srgb, var(--color-brand-ink) 42%, transparent)',
        zIndex: 50,
        padding: 'var(--space-4)',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex flex-col w-full"
        style={{
          maxWidth: lightbox ? '64rem' : '30rem',
          maxHeight: lightbox ? '100%' : 'min(80vh, 40rem)',
          background: lightbox ? 'transparent' : 'var(--color-surface-raised)',
          borderRadius: lightbox ? undefined : 'var(--radius-lg)',
          boxShadow: lightbox ? undefined : 'var(--elevation-overlay)',
        }}
      >
        <div
          className="flex items-center justify-between gap-3 shrink-0"
          style={{
            padding: lightbox ? '0 0 var(--space-3)' : 'var(--space-4) var(--space-4) var(--space-3)',
            borderBottom: lightbox ? undefined : '1px solid var(--color-border-subtle)',
          }}
        >
          <h2
            className="t-body-strong"
            style={{
              // On the scrim rather than on paper, so the title takes the inverse ink.
              color: lightbox ? 'var(--color-text-on-inverse)' : 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center shrink-0 rounded-md"
            style={{
              minHeight: 'var(--tap-min)',
              minWidth: 'var(--tap-min)',
              background: 'none',
              border: 'none',
              color: lightbox ? 'var(--color-text-on-inverse)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div
          className="flex-1"
          style={{ overflowY: 'auto', padding: lightbox ? 0 : 'var(--space-4)' }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
